import { CreateTopicRequest, IntegrationException, TagCategoryDataType, TopicCategory, UserTag } from '../../types';
import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators';

import { DEFAULT_CATEGORY_ICON } from '../../helpers/constants';
import { Menu } from '@material/mwc-menu';
import { StateController } from '../../helpers/state/state-management/state-controller';
import { createTopicRequestFromTopic } from '../../helpers/api/topics-api';
import { instanceOfTagGroup } from '../../helpers/tags-categories';
import { logWithAmplitude } from '../../helpers/api/api';
import pointCache from '../../helpers/state/point-cache';
import topicData from '../../helpers/state/topic-data';
import { getMainPage } from '../../helpers/page-name';

@customElement('admin-categories-tags-fragment')
export class AdminCategoriesTagsFragment extends LitElement {
    amplitude_callsite: string = 'Edit Categories Drawer';

    @query('#tagsMenu')
    tagsMenu!: Menu;

    @query('#tagsMenuTrigger')
    tagsMenuTrigger!: HTMLDivElement;

    @state()
    showCategoriesDrawer: boolean = false;

    @state()
    showTagsDrawer: boolean = false;

    @state()
    showTagGroupsDrawer: boolean = false;

    @state()
    activeCategory?: TopicCategory;

    @state()
    activeDrawer?: TagCategoryDataType;

    @state()
    activeTag?: UserTag;

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);

    async firstUpdated() {
        this.tagsMenu.anchor = this.tagsMenuTrigger;
    }

    //
    async bulkAddCategories() {
        // text pop up to ask for comma separated values
        const input = prompt('Enter comma separated values for name of new categories:');
        if (input) {
            const values = input.split(',').map(item => item.trim());
            // Call a function to handle the bulk addition of tags
            await this.handleBulkAddCategories(values);
        }
    }

    async handleBulkAddCategories(values: string[]) {
        const newCategories: TopicCategory[] = [];
        values.forEach(value => {
            const newCategory: TopicCategory = {
                _id: undefined,
                name: value,
                description: '',
                icon: DEFAULT_CATEGORY_ICON,
                can_crowdsource: true,
                icon_size: undefined,
            };
            newCategories.push(newCategory);
        });
        await topicData.createNewCategories(newCategories);
    }

    async bulkAddSingleTags() {
        // text pop up to ask for comma separated values
        const input = prompt('Enter comma separated values for name of new tags:');
        if (input) {
            const values = input.split(',').map(item => item.trim());
            // Call a function to handle the bulk addition of tags
            await this.handleBulkAddTags(values);
        }
    }

    async bulkAddSingleTagsGroupTopLevel() {
        // text pop up to ask for comma separated values
        const input = prompt('Enter comma separated values for name of new tag groups. This will create top-level tag groups. You can add sub-tags later.');
        if (input) {
            const values = input.split(',').map(item => item.trim());
            // Call a function to handle the bulk addition of tags
            await this.handleBulkAddTagsGroupTopLevel(values);
        }
    }

    async handleBulkAddTags(values: string[]) {
        for (const value of values) {
            await topicData.createNewUserTag(false, this.amplitude_callsite, value);
        }
    }

    async handleBulkAddTagsGroupTopLevel(values: string[]) {
        for (const value of values) {
            await topicData.createNewUserTag(true, this.amplitude_callsite, value);
        }
    }

    protected render() {
        return html`
            <div class="tags-categories__container">
                <h4 class="sub-title">
                    Categories
                </h4>
                <p class="info-paragraph">
                    Click on a category to edit and delete. To delete a category, delete all associated points. Click and drag the categories to reorder them.
                    <span style="color: var(--main-primary-color); cursor: pointer; font-weight: 400; text-decoration: underline;" @click="${this.bulkAddCategories}"><i>Bulk Add Categories</i></span>
                </p>

                <tags-categories-wrapper
                    ?isCategory=${true}
                    @dataAction=${this.openCategory}
                    .data=${topicData.categories}
                    .activeData=${this.activeCategory}
                    .inactiveButtons=${this.showCategoriesDrawer}
                    ?showNew=${true}
                    .newDisabled=${this.showCategoriesDrawer}
                    @newClick=${this.createNewCategory}
                    @updateData=${this.updateReorderedItems}
                    >
                </tags-categories-wrapper>

                ${this.showCategoriesDrawer
                    ? html`
                    <edit-categories-tags
                        dataType="${TagCategoryDataType.Category}"
                        .data="${this.activeCategory}"
                        @closeDrawer="${this.closeCategory}">
                    </edit-categories-tags>`
                    :
                    ''
                }

                ${topicData.hasExceptions !== IntegrationException.WelcomeHomeRelocations ? html`
                    <h4 class="sub-title">
                        Tags and Tag Groups
                    </h4>
                    <p class="info-paragraph">Tags and tag groups are an optional way to further segment your points. Click on a tag to edit and delete. Click and drag the tags to reorder them.
                        <br><span style="color: var(--main-primary-color); cursor: pointer; font-weight: 400; text-decoration: underline;" @click="${this.bulkAddSingleTags}"><i>Bulk Add Tags</i></span> |
                        <span style="color: var(--main-primary-color); cursor: pointer; font-weight: 400; text-decoration: underline;" @click="${this.bulkAddSingleTagsGroupTopLevel}"><i>Bulk Add Tag Groups</i></span>
                    </p>
                    <tags-categories-wrapper
                        ?isCategory=${false}
                        @dataAction=${this.openTag}
                        .data=${topicData.topLevelUserTags}
                        .activeData=${this.activeTag}
                        @updateData=${this.updateReorderedItems}
                        ?showNew=${true}
                        @newClick=${this.openTagDropdown}
                        .newDisabled="${this.showTagsDrawer || this.showTagGroupsDrawer}">
                    </tags-categories-wrapper>

                    <div style="position: relative;">
                        <mwc-menu id="tagsMenu">
                            <mwc-list-item @click="${this.createNewTag}">New Tag</mwc-list-item>
                            <mwc-list-item @click="${this.createNewTagGroup}">New Tag Group</mwc-list-item>
                        </mwc-menu>
                    </div>

                    ${this.showTagsDrawer
                        ? html`
                        <edit-categories-tags
                            dataType="${TagCategoryDataType.Tag}"
                            .data="${this.activeTag}"
                            @closeDrawer="${this.closeTag}">
                        </edit-categories-tags>`
                        :
                        ''
                    }
                    ${this.showTagGroupsDrawer
                        ? html`
                        <edit-categories-tags
                            dataType="${TagCategoryDataType.TagGroup}"
                            .data="${this.activeTag}"
                            @closeDrawer="${this.closeTagGroup}">
                        </edit-categories-tags>`
                        :
                        ''
                    }
                ` : ''}
            </div>

            <error-toast id="errorToast"></error-toast>
        `;
    }

    private async createNewCategory() {
        await topicData.createNewCategory();
        this.showCategoriesDrawer = true;
        this.activeCategory = topicData.categories[topicData.categories.length - 1];

        logWithAmplitude(
            this.amplitude_callsite,
            'Add Category Button Click',
            {
                topic_id: topicData.topicId,
                point_key: topicData.topicKey,
                category_number: topicData.categories.length,
            }
        );
    }


    openTagDropdown() {
        this.tagsMenu.open = true;
    }

    closeCategory(){
        this.showCategoriesDrawer = false;
        this.activeCategory = undefined;
    }

    closeDrawer() {
        switch(this.activeDrawer) {
            case TagCategoryDataType.Category:
                this.closeCategory();
                break;

            case TagCategoryDataType.Tag:
                this.closeTag();
                break;

            case TagCategoryDataType.TagGroup:
                this.closeTagGroup();
                break;
        }
    }

    openCategory(e: CustomEvent){
        this.showCategoriesDrawer = true;
        this.activeCategory = e.detail.data;
    }

    private async createNewTag() {
        const newtag = await topicData.createNewUserTag();
        this.activeTag = newtag;
        this.showTagsDrawer = true;
        this.showTagGroupsDrawer = false;
        this.tagsMenu.open = true;

        logWithAmplitude(
            this.amplitude_callsite,
            'Add Category Button Click',
            {
                topic_id: topicData.topicId,
                point_key: topicData.topicKey,
                tag_number: topicData.allUserTags.length,
            }
        );
    }

    openTag(e: CustomEvent){
        const t = e.detail.data;

        if (instanceOfTagGroup(t)) {
            this.showTagGroupsDrawer = true;
            this.showTagsDrawer = false;
        } else {
            this.showTagsDrawer = true;
            this.showTagGroupsDrawer = false;
        }
        this.activeTag = t;
    }

    closeTag(){
        this.showTagsDrawer = false;
        this.activeTag = undefined;
    }

    private async createNewTagGroup() {
        await topicData.createNewUserTag(true);
        this.activeTag = topicData.topLevelUserTags[topicData.topLevelUserTags.length - 1];
        this.showTagGroupsDrawer = true;

        logWithAmplitude(
            this.amplitude_callsite,
            'Add Category Button Click',
            {
                topic_id: topicData.topicId,
                point_key: topicData.topicKey,
                tag_number: topicData.allUserTags.length,
            }
        );
    }

    closeTagGroup(){
        this.showTagGroupsDrawer = false;
        this.activeTag = undefined;
    }

    async updateReorderedItems(e: CustomEvent) {
        const updatedTopic = {
            ...createTopicRequestFromTopic(topicData.topic),
        };

        if (e.detail.isCategory === true) {
            updatedTopic.categories = e.detail.data;
        } else {
            updatedTopic.tag_info = { user_tags: e.detail.data };
        }

        await this.updateTopicData(updatedTopic);
    }

    async updateTopicData(updatedTopic: CreateTopicRequest) {
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            const newTopic = await topicData.fetchAndUpdateTopicData(updatedTopic, this.amplitude_callsite);

            await pointCache.instantiatePointCache(newTopic, topicData.topicKey, true);

        }, 'An error has occurred.  Context: could not refresh the topic after successful updates.');

        this.closeDrawer();
    }

    static get styles() {
        return css`

            .title {
                color: var(--main-primary-color);
                text-align: center;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                margin: 5px 0;
                text-align: left;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }

            .sub-title {
                margin-bottom: 5px;
                color: var(--theme-input-color);
                font-size: 0.9rem;
            }

            mwc-icon-button.categories__add {
                --mdc-icon-button-size: 76px;
                background-color: green;
                color: var(--theme-input-color);
                border-radius: 100px;
                margin: 0 6px;
                box-shadow: 0px 2px 2px -2px rgba(31, 27, 45, 0.08),
                    0px 4px 12px rgba(31, 27, 45, 0.08);
            }

            .new-tag-category-button {
                background-color: white;
                border: 1px solid lightgray;
                border-radius: 50px;
                padding: 4px 4px;
                margin: 4px;
                cursor: grab;
                display: flex;
                align-items: center;
                --mdc-icon-size: 16px;
            }

            .tags-categories__container {
                flex-direction: column;
                height: 100%;
                overflow: hidden auto;
                padding-right: 20px;
            }

            /* width */
            .tags-categories__container::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .tags-categories__container::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .tags-categories__container::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .tags-categories__header {
                color: var(--main-primary-color);
                margin-top: 1rem;
            }

            @media only screen and (max-width: 550px) {
                .tags-categories__header {
                    margin: 0.2em 0;
                }
            }
        `;
    }
}
